import React from 'react';
import { X } from 'lucide-react';

const Rule = ({ title, children }) => (
  <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
    <h4 className="text-sm font-bold text-gray-800">{title}</h4>
    <div className="mt-1 text-xs leading-5 text-gray-600">{children}</div>
  </div>
);

const SalaryHelpModal = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" role="presentation">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="salary-help-title"
        className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div>
            <h3 id="salary-help-title" className="text-lg font-bold text-gray-900">How salary and wage cycles work</h3>
            <p className="mt-1 text-xs text-gray-500">The same rules drive Salary, Expenses and Staff Dashboard figures.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100" aria-label="Close salary help">
            <X size={18} />
          </button>
        </header>
        <div className="space-y-3 overflow-y-auto p-5">
          <Rule title="Salary cycle and due date">
            A cycle starts on one salary due date and ends one day before the next due date. Earnings from that cycle become due on the next due date.
          </Rule>
          <Rule title="Monthly salaried staff">
            Salary accrues at monthly salary divided by 30. A complete cycle is capped at one monthly salary. A 31st cycle day adds nothing. In a 28 or 29 day cycle, the final cycle day tops the accrued amount up to the full monthly salary. Joining or leaving part-way through a cycle remains actual-days pro-rata.
          </Rule>
          <Rule title="Daily-wage staff">
            The daily rate is the monthly wage reference divided by 30. Only days with both an In and an Out punch earn wages. Paid and unpaid off-days and deductions remain separately visible.
          </Rule>
          <Rule title="Advance versus salary payment">
            Every wage payment first clears the oldest unpaid salary. Once older dues are clear, a payment before the next due date is an advance for that next cycle. A payment on or after the due date clears that cycle as salary. Any amount beyond the outstanding salary becomes an advance for the following cycle.
          </Rule>
          <Rule title="Manual type warning">
            The Expense form still records the selected type. If that type disagrees with what the payment actually settles, the expandable record is highlighted red and explains how it was counted. The financial amount is not lost or counted twice.
            For example: Recorded as Salary; counted as Advance.
          </Rule>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <h4 className="text-sm font-bold text-emerald-900">Case study: Sayam, Rs.48K salaried</h4>
              <ul className="mt-2 space-y-1 text-xs leading-5 text-emerald-900/80">
                <li>20 Jun to 19 Jul earns Rs.48K.</li>
                <li>Rs.13K on 24 Jun clears the older May/Jun due.</li>
                <li>Rs.5K on 30 Jun and Rs.10K on 19 Jul are advances.</li>
                <li>Rs.10K on 20 Jul, Rs.20K on 22 Jul and Rs.3K on 23 Jul are salary payments.</li>
                <li>Rs.15K advances + Rs.33K salary = Rs.48K, leaving zero.</li>
              </ul>
            </div>
            <div className="rounded-xl border border-sky-200 bg-sky-50 p-4">
              <h4 className="text-sm font-bold text-sky-900">Case study: Wasif, daily wage</h4>
              <ul className="mt-2 space-y-1 text-xs leading-5 text-sky-900/80">
                <li>Rs.30K wage reference gives a Rs.1K daily rate.</li>
                <li>26 completed attendance days earn Rs.26K.</li>
                <li>Rs.2.5K deductions leave Rs.23.5K.</li>
                <li>Rs.10K advance and Rs.13.5K salary settle the cycle.</li>
                <li>The ending over/underpayment is zero.</li>
              </ul>
            </div>
          </div>
        </div>
        <footer className="flex justify-end border-t border-gray-200 px-5 py-3">
          <button type="button" onClick={onClose} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">Close</button>
        </footer>
      </section>
    </div>
  );
};

export default SalaryHelpModal;
